import React, { useCallback, useState } from 'react';
import DebtClockDigits from 'components/DebtClock/Digits/Digits';
import useDebtClockConfig from 'hooks/useDebtClockConfig';
import styles from './DebtClockCard.module.css';

const COPY = {
  en: {
    nationalCard: 'National debt card',
    provincialCard: 'Provincial debt card',
    govCanada: 'Government of Canada',
    currentBalance: 'Current balance',
    cardholder: 'Cardholder',
    everyCanadian: 'Every Canadian taxpayer',
    everyTaxpayer: 'Every taxpayer',
    yourShare: 'Your share',
    share: 'Share',
    copied: 'Link copied',
  },
  fr: {
    nationalCard: 'Carte de dette nationale',
    provincialCard: 'Carte de dette provinciale',
    govCanada: 'Gouvernement du Canada',
    currentBalance: 'Solde actuel',
    cardholder: 'Titulaire',
    everyCanadian: 'Chaque contribuable canadien',
    everyTaxpayer: 'Chaque contribuable',
    yourShare: 'Votre part',
    share: 'Partager',
    copied: 'Lien copié',
  },
};

function MapleLeafIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2c.6 2.1 1.2 3.1 2.4 3.6l-.9.9 2.7-.3-.5 2.4 2.3-1-.4 2.6L21 11l-1.4 1.3 1.1.9-3.3 1 .6 1.9-2.8-1 .2 3.1L12 16.2l-3.4 2 .2-3.1-2.8 1 .6-1.9-3.3-1 1.1-.9L3 11l3.9.5-.4-2.6 2.3 1-.5-2.4 2.7.3-.9-.9C11.3 5.1 11.9 4.1 12 2z"
        fill="#7a0f14"
      />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" />
      <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
    </svg>
  );
}

function DebtClockCard({ province, language, debt_data }) {
  const { debtConfigState, MAIN_INTERVAL } = useDebtClockConfig(
    province,
    debt_data
  );
  const [shareFeedback, setShareFeedback] = useState('');

  const currentClock = debt_data ? debt_data[province] : null;
  const isFrench = String(language || 'en').toLowerCase().startsWith('fr');
  const copy = COPY[isFrench ? 'fr' : 'en'];
  const isFederal = String(province || '').toLowerCase() === 'federal';

  const issuer = isFederal
    ? copy.govCanada
    : String(
        (isFrench && currentClock?.clockTitleFr) ||
          currentClock?.clockTitle ||
          province ||
          ''
      );
  const cardLabel = isFederal ? copy.nationalCard : copy.provincialCard;
  const cardholder = isFederal ? copy.everyCanadian : copy.everyTaxpayer;

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const title = document.title || copy.nationalCard;
    const text = `${title} — Canadian Taxpayers Federation`;
    const shareData = { title, text, url };

    const copyFallback = async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          setShareFeedback(copy.copied);
          window.setTimeout(() => setShareFeedback(''), 2000);
          return;
        }
      } catch (err) {
        // Fall through to legacy copy.
      }
      try {
        const input = document.createElement('input');
        input.value = url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setShareFeedback(copy.copied);
        window.setTimeout(() => setShareFeedback(''), 2000);
      } catch (err) {
        // Ignore clipboard failures silently.
      }
    };

    if (typeof navigator.share === 'function') {
      const canShare =
        typeof navigator.canShare !== 'function' || navigator.canShare(shareData);
      if (canShare) {
        try {
          await navigator.share(shareData);
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') {
            return;
          }
        }
      }
    }

    await copyFallback();
  }, [copy.copied, copy.nationalCard]);

  if (!currentClock) {
    return null;
  }

  return (
    <div className={styles.card}>
      <div className={styles.row}>
        <div className={styles.headerText}>
          <div className={`${styles.emb} ${styles.issuer}`}>{issuer}</div>
          <div className={`${styles.lbl} ${styles.cardLabel}`}>{cardLabel}</div>
        </div>
        <div className={styles.leaf}>
          <MapleLeafIcon />
        </div>
      </div>

      <div className={styles.chipRow}>
        <span className={styles.contactless} aria-hidden="true">
          ))
        </span>
        <button
          type="button"
          className={styles.goldShare}
          onClick={handleShare}
        >
          <ShareIcon />
          {shareFeedback || copy.share}
        </button>
      </div>

      <div className={styles.balanceBlock}>
        <div className={`${styles.lbl} ${styles.balanceLabel}`}>
          {copy.currentBalance}
        </div>
        <div className={styles.balanceDigits}>
          {debtConfigState.debtMain != null && (
            <DebtClockDigits
              interval={MAIN_INTERVAL}
              initialDebt={debtConfigState.debtMain}
              incrementRate={debtConfigState.incrementRateMain || 0}
              isFederal
              slideUp={(debtConfigState.incrementRateMain || 0) > 0}
            />
          )}
        </div>
      </div>

      <div className={styles.rowBottom}>
        <div className={styles.shareBlock}>
          <div className={`${styles.lbl} ${styles.shareLabel}`}>
            {copy.yourShare}
          </div>
          <div className={styles.shareDigits}>
            {debtConfigState.debtShare != null &&
              debtConfigState.population > 0 && (
                <DebtClockDigits
                  interval={MAIN_INTERVAL}
                  initialDebt={debtConfigState.debtShare}
                  incrementRate={debtConfigState.incrementRateShare || 0}
                  isFederal
                  slideUp={(debtConfigState.incrementRateShare || 0) > 0}
                />
              )}
          </div>
        </div>
        <div className={styles.cardholderBlock}>
          <div className={`${styles.lbl} ${styles.cardholderLabel}`}>
            {copy.cardholder}
          </div>
          <div className={`${styles.emb} ${styles.cardholderName}`}>
            {cardholder}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebtClockCard;
